/**
 * Stub for useBreakpoint. App/shell provides the real implementation (e.g. @appic/hooks).
 */
import * as React from 'react'

export interface UseBreakpointReturn {
  breakpoint: 'mobile' | 'tablet' | 'laptop' | 'desktop' | 'ultrawide'
  width: number
  height: number
  orientation: 'portrait' | 'landscape'
  isMobile: boolean
  isTablet: boolean
  isLaptop: boolean
  isDesktop: boolean
  isUltrawide: boolean
  isSm: boolean
  isMd: boolean
  isLg: boolean
  isXl: boolean
  is2Xl: boolean
  isMobileOrTablet: boolean
  isLaptopOrAbove: boolean
  isDesktopOrAbove: boolean
  isPortrait: boolean
  isLandscape: boolean
}

export function useBreakpoint(): UseBreakpointReturn {
  const [dimensions, setDimensions] = React.useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  })

  React.useEffect(() => {
    const update = () =>
      setDimensions({ width: window.innerWidth, height: window.innerHeight })
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  const w = dimensions.width
  const breakpoint =
    w < 768 ? 'mobile' : w < 1024 ? 'tablet' : w < 1920 ? 'laptop' : w < 2560 ? 'desktop' : 'ultrawide'
  return {
    breakpoint,
    width: dimensions.width,
    height: dimensions.height,
    orientation: dimensions.width >= dimensions.height ? 'landscape' : 'portrait',
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isLaptop: breakpoint === 'laptop',
    isDesktop: breakpoint === 'desktop',
    isUltrawide: breakpoint === 'ultrawide',
    isSm: w >= 640,
    isMd: w >= 768,
    isLg: w >= 1024,
    isXl: w >= 1280,
    is2Xl: w >= 1536,
    isMobileOrTablet: breakpoint === 'mobile' || breakpoint === 'tablet',
    isLaptopOrAbove: ['laptop', 'desktop', 'ultrawide'].includes(breakpoint),
    isDesktopOrAbove: ['desktop', 'ultrawide'].includes(breakpoint),
    isPortrait: dimensions.width < dimensions.height,
    isLandscape: dimensions.width >= dimensions.height,
  }
}
